import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useHeader } from "../../Providers/HeaderContextProvider";
import {
  EXPLORE_FEED_INVALIDATED,
  getExploreFeed,
  getPreparation,
  preparationPollMs,
  shouldShowPreparing,
  PREPARATION_STATUS,
} from "../../Services/explore";
import { openCard } from "./actions";
import { dialogueDestinations } from "./launch";
import { EXPLORE_COPY, MODALITY_COPY } from "./copy";
import ModalitySheet from "./ModalitySheet";
import PreparingView from "./PreparingView";
import FeedSections from "./sections";
import { useLifeAreas } from "./lifeAreas";
import "./style.css";
import "./search/search.css";

/**
 * Explore — one of the three home sections (Figma section 9154:8266).
 *
 * Which view renders is decided by the server, not by local state: a member
 * with no sections yet sees the Preparing page (guide §6.1 rule 7), and so
 * does one whose `/explore/preparation` run is still live — the feed can have
 * sections before its Dialogues are generated (see `shouldShowPreparing`).
 * While Preparing shows, the checklist is polled; when it turns READY the
 * feed is fetched again and replaces the page.
 *
 * Background comes from BackgroundWrapper in AppRoutes, never from here.
 */
function Explore() {
  useLifeAreas(); // re-render once the Life Areas arrive
  const { setHeaderContent, setHeaderContentRightSideContent } = useHeader();
  const navigate = useNavigate();

  const [feed, setFeed] = useState(null);
  const [prep, setPrep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // The Active Dialogue waiting on a Voice / Text choice.
  const [continuing, setContinuing] = useState(null);

  useEffect(() => {
    setHeaderContent(true);
    setHeaderContentRightSideContent(true);
  }, [setHeaderContent, setHeaderContentRightSideContent]);

  // A failed checklist read is not a failed Explore: it only means the page
  // falls back to the sections rule.
  const readPreparation = useCallback(
    () =>
      getPreparation().catch((e) => {
        console.error("Failed to load the preparation checklist", e);
        return null;
      }),
    []
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [data, preparation] = await Promise.all([
          getExploreFeed(),
          readPreparation(),
        ]);
        if (cancelled) return;
        setFeed(data || null);
        setPrep(preparation || null);
      } catch (e) {
        console.error("Failed to load the Explore feed", e);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [readPreparation]);

  // Before activation completes the server says NOT_STARTED (gaps E9): the
  // member goes back to finish activation, as ActivationGatedRoute does for a
  // pending profile — this covers a cached profile that is out of date.
  const notStarted = feed?.preparation_status === PREPARATION_STATUS.NOT_STARTED;
  useEffect(() => {
    if (notStarted) navigate("/dashboard", { replace: true });
  }, [notStarted, navigate]);

  // A Dialogue paused / archived elsewhere makes the Active rail stale: re-read
  // the feed quietly (the page stays as it is if that read fails).
  useEffect(() => {
    const onInvalidated = async () => {
      try {
        const data = await getExploreFeed();
        if (data) setFeed(data);
      } catch (e) {
        console.error("Failed to refresh the Explore feed", e);
      }
    };
    window.addEventListener(EXPLORE_FEED_INVALIDATED, onInvalidated);
    return () => window.removeEventListener(EXPLORE_FEED_INVALIDATED, onInvalidated);
  }, []);

  const preparing = !loading && !error && !notStarted && shouldShowPreparing(feed, prep);

  // Poll the checklist while Preparing shows (paused while the tab is hidden).
  // Each tick re-reads it; once it stops holding the page, the feed is read
  // again so the member lands on what was just built.
  useEffect(() => {
    if (!preparing) return undefined;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      if (document.hidden) {
        timer = setTimeout(tick, preparationPollMs(prep));
        return;
      }
      const next = await readPreparation();
      if (cancelled) return;
      if (next && next.status !== prep?.status) {
        try {
          const data = await getExploreFeed();
          if (!cancelled && data) setFeed(data);
        } catch (_) {
          /* keep the page; the next tick tries again */
        }
      }
      if (!cancelled) setPrep(next || prep);
    };
    timer = setTimeout(tick, preparationPollMs(prep));
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [preparing, prep, readPreparation]);

  // Voice auto-starts on /dashboard?mode=voice; text goes straight into the
  // conversation — the same two destinations as the Aris tab's chooser.
  const talkVoice = () => navigate("/dashboard?mode=voice");
  const talkText = () => navigate("/chat");

  const chooseModality = (mode) => {
    const routes = dialogueDestinations({ instanceId: continuing?.dialogue_instance_id });
    setContinuing(null);
    if (routes) navigate(routes[mode], { state: { dialogueTitle: continuing?.title || null } });
  };

  const handlers = {
    onTalkVoice: talkVoice,
    onTalkText: talkText,
    // Platform actions route by `action`; Dialogue cards open the Detail by
    // `dialogue_id`. `href` is never followed (guide §6.2).
    onOpenDialogue: (card) => openCard(card, navigate),
    onContinueInstance: (item) => setContinuing(item),
    // An Active card opens its Detail by `dialogue_id` (template or CDLG-…).
    onOpenActive: (item) =>
      navigate(`/explore/dialogue/${encodeURIComponent(item.dialogue_id)}`),
    onSeeAllPersonal: () => navigate("/explore/my-dialogues"),
    onViewAllActive: (route) => navigate(route),
    onCapture: () => navigate("/capture"),
    // Self-Knowledge is the one specialised area (aris-ai serves it from
    // /self-knowledge, not /life-areas/{key}) — it gets its own page.
    onOpenLifeArea: (key) =>
      navigate(
        key === "SELF_KNOWLEDGE"
          ? "/explore/self-knowledge"
          : `/explore/life-areas/${key}`
      ),
  };

  return (
    <div className="explore-page">
      <div className="explore-page__inner">
        <header className="ex-header">
          <h1 className="explore-page__title">{EXPLORE_COPY.title}</h1>
          {!loading && !error && !preparing && (
            <p className="ex-header__subtitle">{EXPLORE_COPY.subtitle}</p>
          )}
        </header>

        {!loading && !error && !preparing && !notStarted && (
          // Opens Explore Search (aris-ai plan 47); the query is typed there.
          <button
            type="button"
            className="ex-home-search"
            onClick={() => navigate("/explore/search")}
          >
            <span className="ex-search__icon" aria-hidden="true" />
            What would you like help with today?
          </button>
        )}

        {loading && (
          <div className="explore-card explore-card--status">Loading…</div>
        )}

        {!loading && error && (
          <div className="explore-card explore-card--status">
            We couldn&apos;t load Explore just now. Please try again.
          </div>
        )}

        {/* An empty feed and a failed request are different states: a member with
            nothing prepared yet gets the preparing narrative, not an error. */}
        {preparing && (
          <PreparingView prep={prep} onTalkWithAris={() => navigate("/dashboard")} />
        )}

        {!loading && !error && !preparing && !notStarted && (
          <FeedSections feed={feed} handlers={handlers} />
        )}
      </div>

      <ModalitySheet
        open={Boolean(continuing)}
        title={MODALITY_COPY.continueTitle}
        onChoose={chooseModality}
        onClose={() => setContinuing(null)}
      />
    </div>
  );
}

export default Explore;
